import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { TrendingUp, AlertTriangle, BarChart3, DollarSign, Lightbulb } from "lucide-react";

interface SupplierResult {
  supplier_name: string;
  total_invoices: number;
  total_invoice_amount: number;
  early_payment_eligible: number;
  early_payment_used: number;
  utilization_rate: number;
  discount_rate_apr: number | null;
  savings_captured: number;
  savings_missed: number;
  missed_invoice_count: number;
}

export interface BacktestAnalysis {
  suppliers: SupplierResult[];
  summary: {
    total_suppliers: number;
    total_invoices: number;
    overall_utilization_rate: number;
    total_savings_captured: number;
    total_savings_missed: number;
    total_money_on_table: number;
  };
  insights: string[];
}

function fmt(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function pct(value: number) {
  return `${Math.round(value)}%`;
}

function aprFmt(value: number | null) {
  if (value == null) return "—";
  return `${value.toFixed(1)}%`;
}

export function BacktestResults({ result }: { result: BacktestAnalysis }) {
  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-1.5 mb-1.5">
            <TrendingUp className="w-3.5 h-3.5 text-success" />
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">
              Savings Captured
            </span>
          </div>
          <p className="text-xl font-bold text-success">
            {fmt(result.summary.total_savings_captured)}
          </p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-1.5 mb-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-warning" />
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">
              Money Left on Table
            </span>
          </div>
          <p className="text-xl font-bold text-warning">
            {fmt(result.summary.total_money_on_table)}
          </p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-1.5 mb-1.5">
            <BarChart3 className="w-3.5 h-3.5 text-primary" />
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">
              Utilization Rate
            </span>
          </div>
          <p className="text-xl font-bold text-foreground">
            {result.summary.total_invoices === 0
              ? "N/A"
              : pct(result.summary.overall_utilization_rate)}
          </p>
          {result.summary.total_invoices === 0 && (
            <p className="text-[10px] text-muted-foreground mt-0.5">No invoices uploaded</p>
          )}
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-1.5 mb-1.5">
            <DollarSign className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">
              Total Invoices
            </span>
          </div>
          <p className="text-xl font-bold text-foreground">{result.summary.total_invoices}</p>
        </Card>
      </div>

      {/* Insights */}
      {result.insights?.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-4 h-4 text-warning" />
            <h4 className="text-sm font-semibold text-foreground">Key Insights</h4>
          </div>
          <ul className="space-y-2">
            {result.insights.map((insight, i) => (
              <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                <span className="w-4 h-4 rounded-full bg-muted flex-shrink-0 flex items-center justify-center text-[9px] font-bold text-foreground mt-0.5">
                  {i + 1}
                </span>
                {insight}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Supplier breakdown table */}
      <Card>
        <div className="p-4 border-b border-border">
          <h4 className="text-sm font-semibold text-foreground">Supplier Breakdown</h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            Early payment utilization and missed savings by supplier
          </p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Supplier</TableHead>
              <TableHead className="text-right">Invoices</TableHead>
              <TableHead className="text-right">Total Amount</TableHead>
              <TableHead className="text-right">Eligible</TableHead>
              <TableHead className="text-right">Used</TableHead>
              <TableHead className="text-right">Utilization</TableHead>
              <TableHead className="text-right">APR</TableHead>
              <TableHead className="text-right">Savings Captured</TableHead>
              <TableHead className="text-right">Savings Missed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.suppliers.map((s, i) => (
              <TableRow key={i}>
                <TableCell className="font-medium text-sm">{s.supplier_name}</TableCell>
                <TableCell className="text-right text-sm">{s.total_invoices}</TableCell>
                <TableCell className="text-right text-sm">{fmt(s.total_invoice_amount)}</TableCell>
                <TableCell className="text-right text-sm">{s.early_payment_eligible}</TableCell>
                <TableCell className="text-right text-sm">{s.early_payment_used}</TableCell>
                <TableCell className="text-right">
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      s.utilization_rate >= 75
                        ? "text-success border-success/30 bg-success/10"
                        : s.utilization_rate >= 40
                          ? "text-warning border-warning/30 bg-warning/10"
                          : "text-destructive border-destructive/30 bg-destructive/10"
                    }`}
                  >
                    {s.total_invoices === 0 ? "N/A" : pct(s.utilization_rate)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right text-sm">{aprFmt(s.discount_rate_apr)}</TableCell>
                <TableCell className="text-right text-sm text-success font-medium">
                  {fmt(s.savings_captured)}
                </TableCell>
                <TableCell className="text-right text-sm text-warning font-medium">
                  {fmt(s.savings_missed)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
